import React, { useRef } from 'react'
import { IGeneralUse } from '../../shared/types/Stabledata.type'
import { Box, Card, CardContent, Container, Divider, Grid, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import palette from '../../theme/palette'
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { isEmpty } from 'lodash'
import dayjs from 'dayjs'
import { useLocales } from '../../hooks/useLocales'
import { formatDate } from '../../shared/utils/DateUtils'
import { TinsightParam } from '../../shared/types/computedKeyParam.type'
import { Icon } from '@iconify/react'

function GeneralUsePage({ data, insightParams }: { data: IGeneralUse, insightParams: TinsightParam[] }) {
    const chartRef = useRef<HighchartsReactRefObject | null>(null);
    const { currentLang } = useLocales()
    const getCategories = () => {
        if (isEmpty(data.statistics_history)) return [];

        return data.statistics_history.map(item =>
            formatDate(item.date, 'MMM YYYY', currentLang as any)
        );
    };

    const getCountHorses = () => {
        if (isEmpty(data.statistics_history)) return []
        return data.statistics_history.map(e => e.horses_count)
    }

    const getCountTrainings = () => {
        if (isEmpty(data.statistics_history)) return []
        return data.statistics_history.map(e => e.trainings_count)
    }

    const options: Highcharts.Options = {
        chart: {
            height: 350,
            animation: false,
            marginTop: 40,
            spacingTop: 0,
            backgroundColor: 'transparent',

            style: {
                overflow: 'visible',
            },
        },
        title: {
            text: "",
            align: 'left',
            style: {
                fontSize: "14px"
            }
        },
        plotOptions: {

            series: {
                states: {
                    hover: {
                        enabled: false,
                    },
                    inactive: {
                        enabled: false,
                        opacity: 1,
                    },
                },
                marker: {
                    symbol: 'circle',
                },
            },
            line: {
                dataLabels: {
                    allowOverlap: true,
                    enabled: true,
                    useHTML: true,
                    formatter: function () {
                        return this.y && `<div style="
                            background: ${palette.light.primary.main};
                            border-radius: 4px;
                            z-index:999;
                            padding: 2px ;
                            color:white;
                            font-size: 10px;
                            white-space: nowrap;
                        ">${this.y}</div>`;
                    },
                    align: 'center', // position relative to point
                    verticalAlign: 'bottom',
                    y: 0
                },
                marker: {
                    enabled: true,
                    radius: 4
                }
            },

            column: {
                grouping: true,
                pointPadding: 0.1,
                groupPadding: 0.1,  // reduce spacing between groups if needed
                borderWidth: 0, // optional: spacing between column groups
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    inside: true,       // place inside the column
                    verticalAlign: 'middle', // vertically center
                    align: 'center',    // horizontally center
                    formatter: function () {
                        return this.y && `<div style="
                            border-radius: 4px;
                            padding: 2px ;
                            color:white;
                            background: rgba(60, 60, 60,.5);
                            font-size: 10px;
                            white-space: nowrap;
                        ">${(this.y)}</div>`;
                    }
                }
            }
        },
        credits: {
            enabled: false,
        },
        xAxis: {
            categories: getCategories(),
            type: 'datetime', // important!
            labels: {
                format: '{value:%b %Y}' // e.g. "Jun 2025"
            },
            minPadding: 0.05,  // add small left padding
            maxPadding: 0.05,  // add small right padding
            gridLineWidth: 0,
            endOnTick: false,
            showLastLabel: true,

            // Ensure enough space for the last label
        },
        legend: {
            enabled: true,
            layout: 'horizontal',
            align: 'left',         // Align to right side
            verticalAlign: 'top',   // Align to top
        },
        yAxis: [{
            startOnTick: false,    // prevent auto-adjust downwards
            endOnTick: false,
            gridLineWidth: 0,// disables horizontal grid lines
            title: {
                text: '',
            },

        }, {
            startOnTick: false,    // prevent auto-adjust downwards
            endOnTick: false,
            gridLineWidth: 0,// disables horizontal grid lines
            title: {
                text: '',
            },
            labels: {
            },
            opposite: true,
        },],
        tooltip: {
            enabled: false,
        },
        series: [
            {
                name: t('stable:countTrainings'),
                data: getCountTrainings(),
                type: 'column',
                yAxis: 0,
                color: 'gray',
            },
            {
                name: t('stable:countHorses'),
                data: getCountHorses(),
                type: 'line',
                color: palette.light.primary.main,
                zIndex: 10,
                yAxis: 1
            },
        ],
    };


    const getParam = (param: string) => {
        let found = insightParams.find(p => p.name === param)
        if (found) {
            return found?.name_translated.concat(` (${found.unit_translated})`)
        }
        return ""
    }


    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                <Typography mb={3} variant="h4" sx={{ color: palette.light.primary.main }}>
                    {t('stable:title:'.concat(data.type))}
                </Typography>
                <img
                    style={{ objectFit: 'contain', width: 55, height: 55, marginRight: 15 }}
                    src="/arioneo_logo_white.png"
                    alt="arioneo logo"
                />
            </Box>
            <Typography fontSize={20} mb={2} variant='subtitle2' color={palette.light.primary.main}>
                {t('stable:title:generalUseChartTitle')}
            </Typography>
            <Box width={'1200px'} display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2} mb={3}>
                <div style={{ minWidth: '900px' }} id="chart-container">
                    <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
                </div>

                <Card sx={{ border: `1px solid ${palette.light.primary.main}`, alignSelf: 'center   ', width: '280px', mr: 6 }} elevation={3}>
                    <CardContent >
                        <Stack direction={'column'} spacing={2}>

                            <Typography fontSize={16} variant='subtitle2'>{t('stable:generalCardTitle')}</Typography>

                            <Box display={'flex'} alignItems={'center'} gap={3}>
                                <Typography variant='subtitle2'>{data.count_trainings_last_6_months}</Typography>
                                <Typography variant='body2'>{t('stable:generalCardCountTrainings')}</Typography>
                            </Box>
                            <Box display={'flex'} alignItems={'center'} gap={3}>
                                <Typography variant='subtitle2'>{data.count_horses_last_6_months}</Typography>
                                <Typography variant='body2'>{t('stable:generalCardCountHorse')}</Typography>
                            </Box>

                            <Typography variant='caption' fontStyle={'italic'}>* {t('stable:generalCardHint')}</Typography>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
            <Stack direction={'column'} spacing={1} >
                <Divider />
                <Typography fontSize={20} mb={3} variant='subtitle2' color={palette.light.primary.main}>
                    {t('stable:generalLastMonthTitle')}
                </Typography>
                <Card>
                    <CardContent>
                        <Box display={'flex'} flexDirection={'row'} flexWrap={'nowrap'} position={'relative'}>

                            <Icon icon='noto:trophy' style={{ position: 'absolute', left: 8, top: 8 }} fontSize={32} />

                            {data.horse_best_records.map(avg => (
                                <Box flex={1} key={avg.parameter_value.parameter_name}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        height="100%"
                                    >
                                        <Typography
                                            variant="body2"
                                            fontSize={16}
                                            textAlign="center"
                                            sx={{
                                                minHeight: '80px', // adjust depending on max expected lines
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {getParam(avg.parameter_value.parameter_name)}
                                        </Typography>

                                        <Typography
                                            variant="h2"
                                            fontWeight={500}
                                            textAlign="center"
                                        >
                                            {avg.parameter_value.formatted_value}
                                        </Typography>

                                        <Typography
                                            variant="h5"
                                            fontWeight={500}
                                            textAlign="center"
                                            sx={{
                                                minHeight: '2.5rem', // optional, to align all names
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {avg.horse.name}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>

            </Stack>
        </>
    )
}

export default GeneralUsePage